import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react'

import { gifDisplayTitle } from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import { useToast } from '../../../hooks'
import {
  BridgeUnavailableError,
  getEvalFixture,
  getEvalResult,
  listEvalResults,
  saveEvalFixture,
  type ResultFileListItem,
} from '../services'
import type {
  FixtureMeta,
  ReportJson,
  RunPick,
} from '../../../../functions/tests/eval/recommendations/types'

import { GifThumbnail } from './GifThumbnail'
import { RecordPeek } from './RecordPeek'

interface FixtureTriageInboxProps {
  gifById: Map<string, Gif>
}

/** A queued unlabeled pick awaiting a verdict, with its display metadata. */
interface QueuedPick {
  gifId: string
  stanceLabel: string | null
  reasoning: string | null
}

type Verdict = 'good' | 'trap' | 'skip'

interface VerdictEntry {
  gifId: string
  verdict: Verdict
}

/** Everything the inbox tracks for one fixture being triaged. */
interface FixtureTriageState {
  fixtureId: string
  /** Fresh copy of the fixture's meta.json loaded from disk. */
  meta: FixtureMeta
  queue: QueuedPick[]
  cursor: number
  history: VerdictEntry[]
}

/**
 * Collect the unlabeled picks for a fixture from a report, enriched with each
 * pick's stance label + reasoning (from the extended `picks` array; falls back
 * to bare ids for older reports that predate the extension).
 */
function unlabeledPicksFor(
  report: ReportJson,
  fixtureId: string,
  currentlyLabeled: Set<string>
): QueuedPick[] {
  const runs = report.results.filter((r) => r.fixtureId === fixtureId)
  const pickMeta = new Map<string, RunPick>()
  const unlabeledOrder: string[] = []
  const seen = new Set<string>()
  for (const run of runs) {
    for (const p of run.picks ?? []) {
      if (!pickMeta.has(p.gifId)) pickMeta.set(p.gifId, p)
    }
    for (const id of run.unlabeledIds) {
      // Re-check against the fixture's CURRENT buckets so anything the author
      // has already labeled (this session or a prior one) drops out.
      if (currentlyLabeled.has(id) || seen.has(id)) continue
      seen.add(id)
      unlabeledOrder.push(id)
    }
  }
  return unlabeledOrder.map((id) => {
    const p = pickMeta.get(id)
    return {
      gifId: id,
      stanceLabel: p?.stanceLabel ?? null,
      reasoning: p?.reasoning ?? null,
    }
  })
}

function labeledSet(meta: FixtureMeta): Set<string> {
  return new Set([
    ...(meta.expected?.traps ?? []),
    ...(meta.expected?.forbidden ?? []),
    ...(meta.expected?.good ?? []),
  ])
}

/**
 * Apply one verdict to a fixture's buckets, returning a new meta object.
 * `trap` → the hard-gate `traps` list; `good` → the optional legacy diagnostic.
 * `good` stays omitted from the JSON until the author actually adds one.
 */
function applyVerdict(
  meta: FixtureMeta,
  gifId: string,
  verdict: Verdict
): FixtureMeta {
  const traps = (meta.expected?.traps ?? []).filter((id) => id !== gifId)
  const forbidden = (meta.expected?.forbidden ?? []).filter(
    (id) => id !== gifId
  )
  const good = (meta.expected?.good ?? []).filter((id) => id !== gifId)
  if (verdict === 'trap') traps.push(gifId)
  else if (verdict === 'good') good.push(gifId)
  const expected: FixtureMeta['expected'] = {
    ...meta.expected,
    traps,
    forbidden,
  }
  if (good.length > 0) expected.good = good
  else delete expected.good
  return { ...meta, expected }
}

export function FixtureTriageInbox({ gifById }: FixtureTriageInboxProps) {
  const toast = useToast()
  const [results, setResults] = useState<ResultFileListItem[]>([])
  const [selectedFile, setSelectedFile] = useState<string>('')
  const [report, setReport] = useState<ReportJson | null>(null)
  const [loading, setLoading] = useState(true)
  const [bridgeDown, setBridgeDown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Which fixtures in the report have unlabeled picks, and how many.
  const [fixtureQueueCounts, setFixtureQueueCounts] = useState<
    { fixtureId: string; count: number }[]
  >([])

  const [active, setActive] = useState<FixtureTriageState | null>(null)
  const [savedFixtures, setSavedFixtures] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [loadingFixture, setLoadingFixture] = useState(false)

  // Load the results file list, default to newest.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setBridgeDown(false)
      setError(null)
      try {
        const list = await listEvalResults()
        if (cancelled) return
        setResults(list)
        setSelectedFile((prev) => prev || list[0]?.file || '')
      } catch (err) {
        if (cancelled) return
        if (err instanceof BridgeUnavailableError) setBridgeDown(true)
        else setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Load the selected report and compute which fixtures have unlabeled picks.
  const loadReport = useCallback(async (file: string) => {
    if (!file) return
    setLoading(true)
    setError(null)
    setActive(null)
    setSavedFixtures(new Set())
    try {
      const data = await getEvalResult(file)
      setReport(data)
      const counts: { fixtureId: string; count: number }[] = []
      for (const f of data.fixtures) {
        const ids = new Set<string>()
        for (const r of data.results.filter((x) => x.fixtureId === f.id)) {
          for (const id of r.unlabeledIds) ids.add(id)
        }
        if (ids.size === 0) continue
        // The report's unlabeledIds are a snapshot from when the eval ran —
        // cross-check against the fixture's CURRENT buckets on disk so picks
        // labeled since then (e.g. a prior triage session) drop out.
        let remaining = ids.size
        try {
          const meta = await getEvalFixture(f.id)
          const labeled = labeledSet(meta)
          remaining = [...ids].filter((id) => !labeled.has(id)).length
        } catch {
          // Fixture unreadable — fall back to the snapshot count.
        }
        if (remaining > 0) {
          counts.push({ fixtureId: f.id, count: remaining })
        }
      }
      setFixtureQueueCounts(counts)
    } catch (err) {
      if (err instanceof BridgeUnavailableError) setBridgeDown(true)
      else setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedFile) void loadReport(selectedFile)
  }, [selectedFile, loadReport])

  const beginFixture = useCallback(
    async (fixtureId: string) => {
      if (!report) return
      setLoadingFixture(true)
      try {
        // Load the fixture's CURRENT meta from disk (not from the report) so
        // verdicts merge onto the latest buckets, not a stale snapshot.
        const meta = await getEvalFixture(fixtureId)
        const queue = unlabeledPicksFor(report, fixtureId, labeledSet(meta))
        if (queue.length === 0) {
          toast.success(`No unlabeled picks left for ${fixtureId}`)
          setSavedFixtures((prev) => new Set(prev).add(fixtureId))
          setFixtureQueueCounts((prev) =>
            prev.filter((c) => c.fixtureId !== fixtureId)
          )
          return
        }
        setActive({ fixtureId, meta, queue, cursor: 0, history: [] })
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        toast.error(`Could not load fixture ${fixtureId}: ${msg}`)
      } finally {
        setLoadingFixture(false)
      }
    },
    [report, toast]
  )

  // Verdicts autosave immediately — navigating away mid-queue must never lose
  // a label. Writes are chained so rapid keyboard verdicts land in order; each
  // write carries the full meta snapshot from its own verdict, so the last
  // write in the chain always persists the latest state.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const persistMeta = useCallback(
    (fixtureId: string, meta: FixtureMeta) => {
      saveChainRef.current = saveChainRef.current.then(async () => {
        setSaving(true)
        try {
          await saveEvalFixture(fixtureId, meta)
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err)
          toast.error(`Autosave failed — verdict not persisted: ${msg}`)
        } finally {
          setSaving(false)
        }
      })
    },
    [toast]
  )

  const doVerdict = useCallback(
    (verdict: Verdict) => {
      if (!active) return
      const pick = active.queue[active.cursor]
      if (!pick) return
      const meta =
        verdict === 'skip'
          ? active.meta
          : applyVerdict(active.meta, pick.gifId, verdict)
      setActive({
        ...active,
        meta,
        cursor: active.cursor + 1,
        history: [...active.history, { gifId: pick.gifId, verdict }],
      })
      if (verdict !== 'skip') persistMeta(active.fixtureId, meta)
    },
    [active, persistMeta]
  )

  const undo = useCallback(() => {
    if (!active || active.history.length === 0) return
    const last = active.history[active.history.length - 1]
    // Recompute buckets by removing the just-added label (skip added nothing).
    let meta = active.meta
    if (last.verdict !== 'skip') {
      const traps = (meta.expected?.traps ?? []).filter(
        (id) => id !== last.gifId
      )
      const good = (meta.expected?.good ?? []).filter((id) => id !== last.gifId)
      const expected: FixtureMeta['expected'] = {
        ...meta.expected,
        traps,
      }
      if (good.length > 0) expected.good = good
      else delete expected.good
      meta = { ...meta, expected }
    }
    setActive({
      ...active,
      meta,
      cursor: Math.max(0, active.cursor - 1),
      history: active.history.slice(0, -1),
    })
    if (last.verdict !== 'skip') persistMeta(active.fixtureId, meta)
  }, [active, persistMeta])

  const finished = active ? active.cursor >= active.queue.length : false
  const pendingVerdicts = active
    ? active.history.filter((h) => h.verdict !== 'skip').length
    : 0

  // Keyboard triage: y=known-good (optional), t=trap, n=skip, u=undo.
  useEffect(() => {
    if (!active || finished) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return
      }
      if (e.key === 'y') doVerdict('good')
      else if (e.key === 't') doVerdict('trap')
      else if (e.key === 'n') doVerdict('skip')
      else if (e.key === 'u') undo()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, finished, doVerdict, undo])

  const currentPick = useMemo(() => {
    if (!active || finished) return null
    return active.queue[active.cursor] ?? null
  }, [active, finished])

  const currentGif = currentPick ? gifById.get(currentPick.gifId) : undefined

  const runLabel = selectedFile.replace(/\.json$/, '')

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-2">
        <div>
          <h2 className="text-lg font-semibold">Triage inbox</h2>
          <p className="text-xs text-default-500">
            Give a verdict to each unlabeled pick an eval run surfaced. y →
            known-good (optional diagnostic), t → trap, n → skip, u → undo.
            Verdicts save to the fixture&apos;s <code>meta.json</code>{' '}
            immediately — safe to leave mid-queue.
          </p>
        </div>
        {results.length > 0 && (
          <Select
            aria-label="Results file"
            size="sm"
            label="Eval run"
            className="max-w-md"
            selectedKeys={selectedFile ? new Set([selectedFile]) : new Set()}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0]
              if (typeof key === 'string') setSelectedFile(key)
            }}
          >
            {results.map((r) => (
              <SelectItem key={r.file}>
                {r.file.replace(/\.json$/, '')}
              </SelectItem>
            ))}
          </Select>
        )}
      </CardHeader>
      <CardBody>
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!loading && bridgeDown && (
          <div className="p-3 rounded bg-warning-50 dark:bg-warning-950/30 text-warning-700 dark:text-warning-300 text-sm">
            Fixture file access requires the dev server — the triage inbox reads
            and writes files under{' '}
            <code className="text-xs">
              functions/tests/eval/recommendations
            </code>
            .
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {!loading && !bridgeDown && !error && results.length === 0 && (
          <p className="text-sm text-default-500">
            No eval result files on disk. Run{' '}
            <code className="text-xs">npm run eval:recommendations</code> in{' '}
            <code className="text-xs">/functions</code> first.
          </p>
        )}

        {!loading &&
          !bridgeDown &&
          !error &&
          report &&
          !active &&
          !loadingFixture && (
            <div className="space-y-3">
              {fixtureQueueCounts.length === 0 ? (
                <p className="text-sm text-default-500">
                  No unlabeled picks in {runLabel} — every returned pick is
                  already bucketed. 🎉
                </p>
              ) : (
                <div className="space-y-2">
                  {fixtureQueueCounts.map(({ fixtureId, count }) => {
                    const done = savedFixtures.has(fixtureId)
                    return (
                      <div
                        key={fixtureId}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-default-200 dark:border-default-100/20"
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-xs truncate">
                            {fixtureId}
                          </p>
                          <p className="text-xs text-default-500 mt-0.5">
                            {count} unlabeled pick{count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {done && (
                            <Chip size="sm" color="success" variant="flat">
                              done
                            </Chip>
                          )}
                          <Button
                            size="sm"
                            color="primary"
                            variant="flat"
                            onPress={() => void beginFixture(fixtureId)}
                          >
                            {done ? 'Re-triage' : 'Triage'}
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

        {loadingFixture && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {active && !finished && currentPick && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs text-default-500">
                {active.fixtureId}
              </p>
              <div className="flex items-center gap-2">
                {saving && (
                  <span className="text-xs text-default-400">saving…</span>
                )}
                <p className="text-xs text-default-400">
                  {active.cursor + 1} / {active.queue.length}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              {currentGif ? (
                <GifThumbnail
                  gif={currentGif}
                  className="w-64 h-64 rounded-lg"
                />
              ) : (
                <div className="w-64 h-64 rounded-lg bg-default-200 flex items-center justify-center text-xs text-default-500 text-center p-4">
                  GIF {currentPick.gifId} is missing from the library
                  <br />
                  (re-seed drift) — skip or label anyway
                </div>
              )}
              {currentGif && (
                <p className="text-sm text-default-600 text-center max-w-md">
                  {gifDisplayTitle(currentGif)}
                </p>
              )}
              {currentPick.stanceLabel && (
                <Chip size="sm" variant="flat" color="primary">
                  stance: {currentPick.stanceLabel}
                </Chip>
              )}
              {currentPick.reasoning && (
                <p className="text-xs text-default-500 text-center max-w-md italic">
                  “{currentPick.reasoning}”
                </p>
              )}
              <p className="font-mono text-[10px] text-default-400">
                {currentPick.gifId}
              </p>

              {/* Peek at the retrieval record (collapsed) — react to the GIF
                  first, then check the record if a pick looks subtly off. Keyed
                  by gifId so it collapses as the pick advances. */}
              <RecordPeek key={currentPick.gifId} gifId={currentPick.gifId} />

              <div className="flex gap-2 flex-wrap justify-center">
                <Button
                  color="warning"
                  variant="flat"
                  onPress={() => doVerdict('trap')}
                >
                  Trap (t)
                </Button>
                <Button
                  color="success"
                  variant="flat"
                  onPress={() => doVerdict('good')}
                >
                  Known-good (y)
                </Button>
                <Button
                  color="default"
                  variant="flat"
                  onPress={() => doVerdict('skip')}
                >
                  Skip (n)
                </Button>
                <Button
                  variant="light"
                  onPress={undo}
                  isDisabled={active.history.length === 0}
                >
                  Undo (u)
                </Button>
              </div>
            </div>
          </div>
        )}

        {active && finished && (
          <div className="space-y-4">
            <p className="text-sm">
              Triaged all {active.queue.length} pick
              {active.queue.length === 1 ? '' : 's'} for{' '}
              <span className="font-mono text-xs">{active.fixtureId}</span> —{' '}
              {pendingVerdicts} label{pendingVerdicts === 1 ? '' : 's'} saved to{' '}
              <code className="text-xs">meta.json</code>.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Chip size="sm" color="warning" variant="flat">
                traps {active.meta.expected?.traps?.length ?? 0}
              </Chip>
              <Chip size="sm" color="success" variant="flat">
                known-good {active.meta.expected?.good?.length ?? 0}
              </Chip>
            </div>
            <div className="flex gap-2">
              <Button
                color="primary"
                isLoading={saving}
                onPress={() => {
                  // Skipped picks stay unlabeled — refresh the list count to
                  // whatever this session left behind.
                  const labeled = labeledSet(active.meta)
                  const remaining = active.queue.filter(
                    (p) => !labeled.has(p.gifId)
                  ).length
                  setFixtureQueueCounts((prev) =>
                    remaining > 0
                      ? prev.map((c) =>
                          c.fixtureId === active.fixtureId
                            ? { ...c, count: remaining }
                            : c
                        )
                      : prev.filter((c) => c.fixtureId !== active.fixtureId)
                  )
                  setSavedFixtures((prev) =>
                    new Set(prev).add(active.fixtureId)
                  )
                  setActive(null)
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
